import { PropsWithChildren, useCallback, useEffect } from 'react';
import Axios from 'api/client';
import { AxiosResponse, InternalAxiosRequestConfig, Method } from 'axios';
import { PostHogEvent, posthogEvents } from 'constants/posthog-events';
import { usePosthog } from 'customHooks/usePosthog';
import { logError } from 'helpers/errors';
import { CaptureResult } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { CampaignType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface RequestPayload {
    type?: CampaignType;
}

interface EventConfig {
    submitted: PostHogEvent;
    success?: PostHogEvent;
    error?: PostHogEvent;
}

type PosthogTrackType = 'submitted' | 'success' | 'error';

interface UrlTrackingConfig {
    pattern: RegExp;
    method: Method;
    track: PosthogTrackType[];
    events: (payload: RequestPayload) => EventConfig | null;
    properties?: (payload: RequestPayload) => CaptureResult['properties'];
}

const TRACKED_URLS: UrlTrackingConfig[] = [
    {
        pattern: /\/campaigns$/,
        method: 'post',
        track: ['success', 'error'],
        events: (payload: RequestPayload): EventConfig | null => {
            const events = {
                fundraise: {
                    submitted: posthogEvents.CAMPAIGN_CREATE_PAGE_SUBMITTED,
                    success: posthogEvents.CAMPAIGN_CREATE_PAGE_SUCCESS,
                    error: posthogEvents.CAMPAIGN_CREATE_PAGE_FAILED,
                },
                collect: {
                    submitted: posthogEvents.CAMPAIGN_CREATE_FORM_SUBMITTED,
                    success: posthogEvents.CAMPAIGN_CREATE_FORM_SUCCESS,
                    error: posthogEvents.CAMPAIGN_CREATE_FORM_FAILED,
                },
                event: {
                    submitted: posthogEvents.CAMPAIGN_CREATE_EVENT_SUBMITTED,
                    success: posthogEvents.CAMPAIGN_CREATE_EVENT_SUCCESS,
                    error: posthogEvents.CAMPAIGN_CREATE_EVENT_FAILED,
                },
                general: null,
                external: null,
            };

            return events[payload.type ?? 'fundraise'];
        },
        properties: (payload: RequestPayload) => ({
            type: payload.type ?? 'fundraise',
        }),
    },
    {
        pattern: /\/users\/\d+\/verify-nonprofit$/,
        method: 'post',
        track: ['error'],
        events: (): EventConfig => ({
            submitted: posthogEvents.ACCOUNT_VERIFY_NONPROFIT_SUBMITTED,
            error: posthogEvents.ACCOUNT_VERIFY_NONPROFIT_FAILED,
        }),
    },
    {
        pattern: /accounts\/\d+\/invites$/,
        method: 'post',
        track: ['error'],
        events: (): EventConfig => ({
            submitted: posthogEvents.USER_CREATE_SUBMITTED,
            error: posthogEvents.USER_CREATE_FAILED,
        }),
    },
    {
        pattern: /accounts\/\d+\/payouts$/,
        method: 'post',
        track: ['error'],
        events: (): EventConfig => ({
            submitted: posthogEvents.PAYOUT_PAID_SUBMITTED,
            error: posthogEvents.PAYOUT_PAID_FAILED,
        }),
    },
    {
        pattern: /campaigns\/\d+\/auctions$/,
        method: 'post',
        track: ['success', 'error'],
        events: (): EventConfig => ({
            submitted: posthogEvents.AUCTION_CREATE_SUBMITTED,
            success: posthogEvents.AUCTION_CREATE_SUCCESS,
            error: posthogEvents.AUCTION_CREATE_FAILED,
        }),
    },
];

const Provider = (props: { children: React.ReactNode }) => {
    const { children } = props;

    const enablePostHog = !!(
        import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_KEY &&
        import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_HOST &&
        import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_UI_HOST
    );

    if (!enablePostHog) {
        return children;
    }

    return (
        <PostHogProvider
            apiKey={import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_KEY}
            options={{
                api_host: import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_HOST,
                ui_host: import.meta.env.VITE_DASHBOARD_PUBLIC_POSTHOG_UI_HOST,
            }}
        >
            <PosthogListener>{children}</PosthogListener>
        </PostHogProvider>
    );
};

const PosthogListener = ({ children }: PropsWithChildren) => {
    const { pushEvent } = usePosthog();

    const handlePostHogTracking = useCallback(
        (
            eventType: PosthogTrackType,
            config?: InternalAxiosRequestConfig,
            errorMessage?: string,
        ) => {
            if (!config) {
                return;
            }

            const url = config.url || '';
            const method = config.method;

            const matchedConfig = TRACKED_URLS.find(
                tracked => tracked.pattern.test(url) && tracked.method === method,
            );

            if (!matchedConfig) {
                return;
            }

            let parsedData: RequestPayload;
            try {
                parsedData =
                    typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
            } catch (e) {
                logError(e as Error);
                return;
            }

            const events = matchedConfig.events(parsedData);

            const shouldTrack =
                eventType === 'submitted' || matchedConfig.track.includes(eventType);

            if (shouldTrack && events?.[eventType]) {
                const eventName = events[eventType];

                const eventProperties = {
                    ...matchedConfig.properties?.(parsedData),
                    ...(errorMessage && { error: errorMessage }),
                };

                pushEvent(eventName, eventProperties);
            }
        },
        [pushEvent],
    );

    useEffect(() => {
        Axios.interceptors.request.use(config => {
            handlePostHogTracking('submitted', config);
            return config;
        });

        Axios.interceptors.response.use(
            (response: AxiosResponse) => {
                handlePostHogTracking('success', response.config);
                return response;
            },
            (error: GivebutterAxiosError) => {
                handlePostHogTracking('error', error.config, error.response?.data.message);
                return Promise.reject(error);
            },
        );
    }, [handlePostHogTracking]);

    return <>{children}</>;
};

export { Provider as PostHogProvider };
